import { useMemo, useState } from "react";
import { PageHeader } from "@/components/admin/shared/PageHeader";
import { AdminTable, type ColDef } from "@/components/admin/shared/AdminTable";
import { TablePagination } from "@/components/admin/shared/TablePagination";
import { PennDropdown } from "@/components/ui/dropdown/PennDropdown";
import { useAnalyticsSummary } from "@/hooks/useAnalyticsSummary";
import type { QuoteTimeBucket, RecentEvent } from "@/api/analyticsSummary";

const formatTs = (ts: string | null): string => {
  if (!ts) return "—";
  const parsed = new Date(ts);
  return Number.isNaN(parsed.getTime()) ? ts : parsed.toLocaleString();
};

const formatHours = (h: number | null): string =>
  h === null || !Number.isFinite(h) ? "—" : `${h.toFixed(2)} h`;

const KpiCard = ({ label, value }: { label: string; value: string | number }) => (
  <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-4 shadow-md bg-white dark:bg-gray-900">
    <p className="text-blue_grey3 mb-1 text-small">{label}</p>
    <p className="text-2xl font-semibold text-blue_grey1">{value}</p>
  </div>
);

const RESP_ALL_VALUE = "all";
const RESP_ALL_LABEL = "All responsibilities";

// DAYS_OPTIONS kept commented in source for the day-range picker that's
// currently disabled in the UI (line 105 onwards). Re-enable when the BE
// supports user-controlled windows.
// const DAYS_OPTIONS = [
//   { value: "7", label: "Last 7 days" },
//   ...
// ] as const;

const cycleRowsFor = (q: {
  overall: QuoteTimeBucket;
  standard: QuoteTimeBucket;
  custom: QuoteTimeBucket;
}) => [
  { label: "Overall", bucket: q.overall },
  { label: "Standard offers", bucket: q.standard },
  { label: "Custom offers (manual items)", bucket: q.custom },
];

const activityColumns: ColDef<RecentEvent & { _key: string }>[] = [
  { header: "Date", render: (r) => formatTs(r.ts) },
  { header: "Event", render: (r) => r.eventName ?? "—" },
  { header: "User", render: (r) => r.userEmail ?? "—" },
  { header: "Path", render: (r) => r.path ?? "—" },
  { header: "Session", render: (r) => r.sessionId ?? "—" },
];

export const AnalyticsPage = () => {
  const [days] = useState<number>(30);
  const [responsibility, setResponsibility] = useState<string>(RESP_ALL_VALUE);
  const [recentPageIndex, setRecentPageIndex] = useState(0);
  const [recentPageSize, setRecentPageSize] = useState(20);

  const params = useMemo(
    () => ({
      days,
      responsibility,
      recentLimit: recentPageSize,
      recentOffset: recentPageIndex * recentPageSize,
    }),
    [days, responsibility, recentPageSize, recentPageIndex],
  );
  const { data, isLoading, isFetching, isError, error } = useAnalyticsSummary(params);

  const responsibilityOptions = useMemo(() => {
    const names = data?.responsibilities ?? [];
    return [
      { value: RESP_ALL_VALUE, label: RESP_ALL_LABEL },
      ...names.map((r) => ({ value: r, label: r })),
    ];
  }, [data?.responsibilities]);

  const recentRows = useMemo(
    () =>
      (data?.recentEvents ?? []).map((e, i) => ({
        ...e,
        _key: `${e.ts ?? "x"}-${e.sessionId ?? "s"}-${i}`,
      })),
    [data?.recentEvents],
  );

  const recentTotal = data?.recentTotal ?? 0;
  const recentHasNext = recentPageIndex * recentPageSize + recentRows.length < recentTotal;

  const cycleRows = data ? cycleRowsFor(data.quoteTime) : [];
  const topEvents = data?.topEvents ?? [];
  const topPaths = data?.topPaths ?? [];
  const recommendations = data?.recommendations ?? [];
  const topPathLabel = topPaths[0]?.path ?? "—";

  return (
    <>
      <PageHeader
        title="Analytics"
        subtitle={`Funnel and usage metrics (last ${days} days).`}
        action={
          <div className="flex items-end gap-3">
            {/* <label className="flex flex-col gap-1">
              <span className="text-blue_grey2 text-small-medium">Days</span>
              <div className="w-44">
                <PennDropdown
                  options={DAYS_OPTIONS as unknown as { value: string; label: string }[]}
                  value={String(days)}
                  onChange={(v) => setDays(Number(v))}
                />
              </div>
            </label> */}
            <label className="flex flex-col gap-1">
              <span className="text-on-navy-secondary text-small-medium">Responsibility</span>
              <div className="w-56">
                <PennDropdown
                  options={responsibilityOptions}
                  value={responsibility}
                  onChange={(v) => {
                    setResponsibility(v || RESP_ALL_VALUE);
                    setRecentPageIndex(0);
                  }}
                  isSearchable
                />
              </div>
            </label>
          </div>
        }
      />

      {isError && (
        <div className="border border-red-200 bg-red-50 text-red-700 rounded-lg p-3 mb-4 text-body">
          Failed to load analytics: {(error as Error)?.message ?? "Unknown error"}
        </div>
      )}

      {/* KPIs */}
      <div className="mb-6">
        <p className="text-on-navy mb-3 text-body-semibold">
          KPIs (last {days} days)
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <KpiCard label="Total events" value={isLoading ? "…" : data?.totalEvents ?? 0} />
          <KpiCard
            label="Unique sessions"
            value={isLoading ? "…" : data?.uniqueSessions ?? 0}
          />
          <KpiCard label="Known users" value={isLoading ? "…" : data?.knownUsers ?? 0} />
          <KpiCard label="Top path" value={isLoading ? "…" : topPathLabel} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
        {/* Quote cycle time */}
        <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-4 shadow-md overflow-x-auto bg-white dark:bg-gray-900">
          <p className="text-blue_grey mb-3 text-body-semibold">
            Quote cycle time (Task to PDF)
          </p>
          <table className="w-full min-w-100 text-body">
            <thead>
              <tr className="text-blue_grey4 text-small">
                <th className="text-left pb-2"></th>
                <th className="text-left pb-2">Avg.</th>
                <th className="text-left pb-2">P50</th>
                <th className="text-left pb-2">Count</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {cycleRows.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-3 text-blue_grey4 text-body">
                    {isLoading ? "Loading…" : "No data"}
                  </td>
                </tr>
              )}
              {cycleRows.map((r) => (
                <tr key={r.label}>
                  <td className="py-2 text-blue_grey2 font-medium">{r.label}</td>
                  <td className="py-2 text-blue_grey">
                    {r.bucket.count === 0 ? "No data" : formatHours(r.bucket.avgHours)}
                  </td>
                  <td className="py-2 text-blue_grey">
                    {r.bucket.count === 0 ? "No data" : formatHours(r.bucket.p50Hours)}
                  </td>
                  <td className="py-2 text-blue_grey">{r.bucket.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Top events */}
        <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-4 shadow-md overflow-x-auto bg-white dark:bg-gray-900">
          <p className="text-blue_grey mb-3 text-body-semibold">Top events</p>
          <table className="w-full min-w-80 text-body">
            <thead>
              <tr className="text-blue_grey4 text-small">
                <th className="text-left pb-2">Event</th>
                <th className="text-right pb-2">Count</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {topEvents.length === 0 && (
                <tr>
                  <td colSpan={2} className="py-3 text-blue_grey4 text-body">
                    {isLoading ? "Loading…" : "No events in this window."}
                  </td>
                </tr>
              )}
              {topEvents.map((e) => (
                <tr key={e.name}>
                  <td className="py-2 text-blue_grey font-mono text-small">{e.name}</td>
                  <td className="py-2 text-right text-blue_grey1 font-medium">{e.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        {/* Top paths */}
        <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-4 shadow-md overflow-x-auto bg-white dark:bg-gray-900">
          <p className="text-blue_grey mb-3 text-body-semibold">Top paths</p>
          <table className="w-full min-w-80 text-body">
            <thead>
              <tr className="text-blue_grey4 text-small">
                <th className="text-left pb-2">Path</th>
                <th className="text-right pb-2">Count</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {topPaths.length === 0 && (
                <tr>
                  <td colSpan={2} className="py-3 text-blue_grey4 text-body">
                    {isLoading ? "Loading…" : "No path data in this window."}
                  </td>
                </tr>
              )}
              {topPaths.map((p) => (
                <tr key={p.path}>
                  <td className="py-2 text-blue_grey font-mono text-small">{p.path}</td>
                  <td className="py-2 text-right text-blue_grey1 font-medium">{p.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Recommendations */}
        <div className="border border-gray-200 dark:border-gray-800 rounded-lg p-4 shadow-md bg-white dark:bg-gray-900">
          <p className="text-blue_grey mb-3 text-body-semibold">
            Recommendations (lead generation)
          </p>
          {recommendations.length === 0 ? (
            <p className="text-blue_grey4 text-body">
              {isLoading ? "Loading…" : "No recommendations yet."}
            </p>
          ) : (
            <ul className="space-y-2">
              {recommendations.map((rec, idx) => (
                <li
                  key={idx}
                  className="text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-body"
                >
                  {rec}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Recent activity */}
      <div>
        <p className="text-on-navy mb-1 text-body-semibold">Recent activity</p>
        <p className="text-blue_grey4 mb-3 text-small">
          {recentTotal} events in the selected window, newest first.
        </p>
        <AdminTable
          columns={activityColumns}
          rows={recentRows}
          keyExtractor={(r) => r._key}
          loading={isLoading}
          emptyMessage="No recent activity."
        />
        <TablePagination
          pageIndex={recentPageIndex}
          pageSize={recentPageSize}
          rowsOnPage={recentRows.length}
          hasNext={recentHasNext}
          busy={isFetching}
          onPrev={() => setRecentPageIndex((p) => Math.max(0, p - 1))}
          onNext={() => setRecentPageIndex((p) => p + 1)}
          onPageSizeChange={(size) => {
            setRecentPageSize(size);
            setRecentPageIndex(0);
          }}
        />
      </div>
    </>
  );
};
